import {
  ButtonBase,
  Checkbox,
  Stack,
  type SxProps,
  Typography,
  useTheme,
} from "@mui/material";
import React, { forwardRef, useCallback, useEffect } from "react";

const CustomUncheckedIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-labelledby="unchecked-icon-title"
  >
    <title id="unchecked-icon-title">未チェック状態のCheckbox</title>
    <rect x="0.5" y="0.5" width="19" height="19" rx="5.5" fill="white" />
    <rect x="0.5" y="0.5" width="19" height="19" rx="5.5" stroke={color} />
  </svg>
);

const CustomCheckedIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-labelledby="checked-icon-title"
  >
    <title id="checked-icon-title">チェック済みのCheckbox</title>
    <rect x="0.5" y="0.5" width="19" height="19" rx="5.5" fill={color} />
    <rect x="0.5" y="0.5" width="19" height="19" rx="5.5" stroke={color} />
    <path
      d="M14.6654 6.5L8.2487 12.9167L5.33203 10"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const BaseCheckbox = forwardRef(
  (
    props: {
      variant?: "outlined" | "plain";
      defaultChecked?: boolean;
      disabled?: boolean;
      size?: "medium" | "small";
      label: string | React.ReactNode;
      error?: boolean;
      helperText?: string;
      value?: boolean;
      onChange?: (checked: boolean) => void;
      onBlur?: () => void;
    },
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const { size, label, error, helperText } = props;
    const variant = props.variant ?? "outlined";
    const theme = useTheme();
    const [isChecked, setIsChecked] = React.useState(props.value ?? false);
    useEffect(() => {
      setIsChecked(props.value ?? false);
    }, [props.value]);

    const handleOnChange = useCallback(() => {
      if (props.onChange) {
        props.onChange(!isChecked);
      }
      setIsChecked(!isChecked);
    }, [props.onChange, isChecked]);

    const status: "checked" | "unchecked" | "error" = error
      ? "error"
      : isChecked
        ? "checked"
        : "unchecked";
    const outlinedSx: SxProps = {
      border: "1px solid",
      borderColor: error
        ? theme.palette.caution[500]
        : isChecked
          ? theme.palette.primary.main
          : theme.palette.grey[300],
      backgroundColor: error
        ? theme.palette.caution[50]
        : isChecked
          ? theme.palette.grey[50]
          : theme.palette.background.paper,
      borderRadius: "12px",
    };
    return (
      <Stack ref={ref}>
        <ButtonBase
          onClick={handleOnChange}
          disabled={props.disabled}
          sx={{ backgroundColor: "transparent" }}
        >
          <Stack
            direction="row"
            justifyContent="start"
            alignItems="center"
            spacing={size === "small" ? 0 : 1}
            sx={{
              minWidth: { xs: "auto", sm: "172px" },
              padding: size === "small" ? "2px" : "10px",
              paddingRight: size === "small" ? "14px" : "20px",
              ...{
                ...(variant === "outlined" ? outlinedSx : {}),
              },
            }}
          >
            <Checkbox
              defaultChecked={props.defaultChecked}
              checked={isChecked}
              icon={
                <CustomUncheckedIcon
                  color={
                    status === "error" ? theme.palette.error.main : "#D0D4D3"
                  }
                />
              }
              checkedIcon={
                <CustomCheckedIcon
                  color={
                    status === "error"
                      ? theme.palette.error.main
                      : theme.palette.primary.main
                  }
                />
              }
              onBlur={props.onBlur}
            />
            <Typography
              variant="body1"
              sx={{
                color: props.error
                  ? theme.palette.caution[500]
                  : props.disabled
                    ? theme.palette.grey[300]
                    : isChecked
                      ? theme.palette.grey[900]
                      : theme.palette.grey[500],
              }}
            >
              {label}
            </Typography>
          </Stack>
        </ButtonBase>
        <Typography
          variant="body2"
          mt="4px"
          sx={{
            color: props.error
              ? theme.palette.caution[500]
              : theme.palette.grey[500],
          }}
        >
          {helperText}
        </Typography>
      </Stack>
    );
  },
);
